import { styled } from '@glitz/react';
import * as React from 'react';
import Link from 'webapp/components/Link';
import { desktopEdgeMarginDecorator, maxContentNormalWidthDecorator } from 'webapp/components/page-layout';
import { absoluteCenterDecorator } from 'webapp/decorators/center';
import { ArrowRight } from 'webapp/components/icons';
import {
  createMinLargeViewportDecorator,
  createMaxSmallViewportDecorator,
  createMinMediumViewportDecorator,
} from 'webapp/viewport';
import { headingDecorator } from 'webapp/decorators/heading';
import { useMatchMobile } from '@avensia/excite';
import { isLightshop, isSvenssons } from 'webapp/theme';
import { LineArrowRight } from 'svenssons/shared/icons';

type PropType = {
  children: React.ReactNode;
  backgroundColor: string;
  heading: string;
  showMoreLink?: string;
  showMoreCfLink?: NordicNest.Api.NordicNestLinkApiModel;
  showMoreText?: string;
  showMoreLinkPosition?: NordicNest.Api.ShowMoreLinkPosition;
  id?: string;
  centerHeader?: boolean;
};

export default React.memo(
  styled(function BaseContentList({
    children,
    backgroundColor,
    heading,
    showMoreLink,
    showMoreCfLink,
    showMoreText,
    showMoreLinkPosition = 'top',
    id,
  }: PropType) {
    const isMobile = useMatchMobile();
    const showMore = Boolean((showMoreLink || showMoreCfLink?.to) && showMoreText);
    showMoreLink = showMoreCfLink?.to ? showMoreCfLink?.to : showMoreLink;

    return (
      <Base id={id} css={{ backgroundColor: backgroundColor }}>
        <Content css={showMore && showMoreDecorator}>
          {(heading || showMoreLink) &&
          <TopContent>
            {heading && <Heading>{heading}</Heading>}
            {!isMobile && showMore && showMoreLinkPosition === 'top' && (
              <ShowMore to={showMoreLink}>
                {showMoreText}
                {isSvenssons || isLightshop ? <LineArrow /> : <ArrowRight />}
              </ShowMore>
            )}
          </TopContent>}
          {children}
          {showMore && (isMobile || showMoreLinkPosition === 'bottom') && (
            <ShowMore to={showMoreLink} css={!isMobile && bottomLinkDecorator}>
              {showMoreText}
              {isSvenssons || isLightshop ? <LineArrow /> : <Arrow />}
            </ShowMore>
          )}
        </Content>
      </Base>
    );
  }),
);

const bottomLinkDecorator = styled({
  padding: { bottom: 0, top: t => t.spacing.xl },
});

const showMoreDecorator = styled({
  paddingBottom: 0,
});

const Base = styled.div({
  width: '100%',
});

const Content = styled.div(
  maxContentNormalWidthDecorator,
  desktopEdgeMarginDecorator,
  createMinMediumViewportDecorator({
    padding: { y: t => t.spacing.xl },
  }),
  createMinLargeViewportDecorator({ padding: { y: t => t.spacing.xxl } }),
  {
    padding: { top: t => t.spacing.xl, bottom: t => t.spacing.xl },
  },
);

const TopContent = styled.div({
  position: 'relative',
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  marginBottom: t => t.spacing.xl,
});

const Arrow = styled(ArrowRight, {
  marginLeft: t => t.spacing.s,
});

const LineArrow = styled(LineArrowRight, {
  marginLeft: t => t.sharedSpacing.s,
  fontSize: t => t.sharedFontSize.xxs,
});

const Heading = styled.h2(
  absoluteCenterDecorator,
  headingDecorator,
  createMaxSmallViewportDecorator({
    fontSize: t => (isSvenssons ? t.fontSize.l : t.fontSize.s),
    width: '100%',
    textAlign: 'center',
  }),
  {
    padding: { x: t => t.sharedSpacing.m },
  },
);

const ShowMore = styled(
  Link,
  createMinMediumViewportDecorator({
    width: 'auto',
    justifyContent: 'flex-end',
  }),
  {
    width: '100%',
    justifyContent: 'center',
    padding: { y: t => t.sharedSpacing.m },
    display: 'flex',
    fontSize: t => t.fontSize.xs,
    alignItems: 'center',
  },
);
